import { styled, alpha } from '@material-ui/core/styles'
import { Paper, Box, TextField, Button, Typography, Chip } from '@material-ui/core'
import { motion } from 'framer-motion'
import { getToneColors } from 'src/views/FileUploadView/components/TonalChip'

// Create motion-enabled Material-UI components
const MotionPaper = motion(Paper)
const MotionBox = motion(Box)
const MotionButton = motion(Button)

export const StyledPaper = styled(MotionPaper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: 'none',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  // Remove fixed minHeight to work within container constraints
  minHeight: 'auto',
  maxHeight: '100%',
  position: 'relative',
  overflow: 'visible',
  overflowX: 'hidden',
  '& .MuiInputBase-root': { color: theme.palette.text.primary },
  '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
  '& .MuiMenuItem-root': { color: theme.palette.text.primary },
  '& .MuiStepper-root': {
    backgroundColor: 'transparent',
    padding: theme.spacing(2, 0),
    '& .MuiStep-root': {
      transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.standard }),
    },
  },
  '& .MuiStepLabel-label': {
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.standard }),
    fontWeight: 400,
    '&.Mui-active': {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    '&.Mui-completed': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  '& .MuiStepIcon-root': {
    color: theme.palette.action.disabled,
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.complex }),
    fontSize: '1.5rem',
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      color: theme.palette.primary.main,
    },
  },
}))

export const SectionHeader = styled(MotionBox)(({ theme }) => ({
  marginBottom: theme.spacing(0),
  position: 'relative',
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    position: 'relative',
  },
}))

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(['border-color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light,
        borderWidth: '1px',
      },
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.standard }),
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      transform: 'translate(14px, -9px) scale(0.85)',
    },
  },
}))

export const StyledButton = styled(MotionButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  padding: '10px 24px',
  textTransform: 'none',
  fontSize: '0.9375rem',
  letterSpacing: 0,
  position: 'relative',
  overflow: 'hidden',
  '&.MuiButton-contained': {
    background: theme.palette.primary.main,
    boxShadow: 'none',
    color: theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.palette.primary.dark,
      boxShadow: 'none',
    },
  },
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.grey[500], 0.4),
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
      boxShadow: 'none',
    },
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}))

export const OptionCard = styled(MotionPaper)(({ theme, active }) => ({
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  padding: '32px 24px',
  border: active ? `2px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '& .MuiSvgIcon-root': {
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.complex }),
    marginBottom: theme.spacing(2),
  },
  '& .MuiTypography-h6': {
    transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.standard }),
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  '& .MuiTypography-body2': {
    color: theme.palette.text.secondary,
  },
}))

// Progress Bar Component
export const ProgressBarContainer = styled(MotionBox)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.background.neutral,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: 'none',
  marginTop: theme.spacing(2),
}))

export const ProgressItem = styled(MotionBox)(({ theme, completed, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: completed
    ? alpha(theme.palette.success.main, 0.12)
    : active
    ? theme.palette.action.selected
    : 'transparent',
  border: completed
    ? `1px solid ${theme.palette.success.main}`
    : active
    ? `1px solid ${theme.palette.primary.main}`
    : '1px solid transparent',
  transition: theme.transitions.create(['all'], { duration: theme.transitions.duration.standard }),
  '& .progress-icon': {
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: completed
      ? theme.palette.success.main
      : active
      ? theme.palette.primary.main
      : theme.palette.action.disabled,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    color: completed ? theme.palette.success.contrastText : theme.palette.primary.contrastText,
    fontWeight: 600,
  },
  '& .progress-text': {
    fontSize: '0.85rem',
    color: completed ? theme.palette.success.main : active ? theme.palette.text.primary : theme.palette.text.secondary,
    fontWeight: completed || active ? 500 : 400,
  },
}))

// Simple, clear animation variants
export const pageVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
}

export const childVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
}

export const cardVariants = {
  initial: (i) => ({
    opacity: 0,
    y: 20,
  }),
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 350,
      damping: 25,
      mass: 0.8,
      delay: i * 0.1,
    },
  }),
  hover: {
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
      mass: 0.6,
    },
  },
  tap: {
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 15,
      mass: 0.5,
    },
  },
}

export const buttonVariants = {
  hover: {
    transition: { duration: 0.1, ease: 'easeInOut' },
  },
  tap: {
    transition: { duration: 0.1, ease: 'easeInOut' },
  },
}

// New smooth table row animation variant
export const tableRowVariants = {
  initial: {
    opacity: 0,
    x: -8,
  },
  // Static target (NOT a function of the row index): rows animate once on mount, but a
  // background refetch that reorders the live-polled file list must NOT re-trigger the
  // slide, which yanked the scrollbar while the user was scrolling. The per-row stagger
  // delay is dropped for the same reason.
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 380,
      damping: 28,
      mass: 0.7,
    },
  },
  hover: {
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 25,
      mass: 0.5,
    },
  },
}

// Smooth search field animation
export const searchFieldVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
      mass: 0.6,
    },
  },
  focus: {
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 20,
      mass: 0.5,
    },
  },
}

// File Selection Styled Components
export const StyledTableContainer = styled(MotionPaper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  boxShadow: 'none',
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.background.neutral,
    '& .MuiTableCell-head': {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.neutral,
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(12.5),
      letterSpacing: '0.01em',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: 'none',
      borderLeft: 'none',
      padding: '16px 18px',
      '&:first-of-type': {
        paddingLeft: '32px',
      },
      '&:last-child': {
        paddingRight: '32px',
      },
    },
  },
  '& .MuiTableCell-root': {
    padding: '12px 18px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    borderLeft: 'none',
    color: theme.palette.text.primary,
    verticalAlign: 'middle',
    '&:first-of-type': {
      paddingLeft: '32px',
    },
    '&:last-child': {
      paddingRight: '32px',
    },
  },
  '& .MuiTableRow-root': {
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      '& .MuiTableCell-root:first-of-type': {
        paddingLeft: '28px',
      },
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&.disabled': {
      backgroundColor: `${theme.palette.background.neutral} !important`,
      pointerEvents: 'none',
      '&:hover': {
        backgroundColor: `${theme.palette.background.neutral} !important`,
      },
      '& .MuiTableCell-root': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .MuiTypography-root': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .MuiRadio-root': {
        color: `${theme.palette.action.disabled} !important`,
      },
    },
  },
}))

export const IndicatorChip = styled(Box)(({ theme, selected, disabled }) => ({
  minWidth: '40px',
  height: '28px',
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  border: 'none',
  backgroundColor: disabled
    ? theme.palette.background.neutral
    : selected
    ? theme.palette.primary.main
    : alpha(theme.palette.grey[500], 0.16),
  color: disabled
    ? theme.palette.text.secondary
    : selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
  transition: 'background-color 0.15s ease',
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  outline: disabled ? `1px dashed ${theme.palette.divider}` : 'none',
  '&:hover': disabled
    ? {}
    : {
        backgroundColor: selected ? theme.palette.primary.dark : alpha(theme.palette.grey[500], 0.28),
      },
  '&:active': disabled ? {} : {},
}))

export const ValidationAlert = styled(MotionBox)(({ theme, type }) => ({
  padding: '16px 24px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: alpha(type === 'error' ? theme.palette.error.main : theme.palette.info.main, 0.12),
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  boxShadow: 'none',
}))

export const SelectionGuideBox = styled(MotionBox)(({ theme }) => ({
  marginTop: '16px',
  padding: '20px 24px',
  backgroundColor: theme.palette.background.neutral,
  borderRadius: '8px',
  border: 'none',
  boxShadow: 'none',
}))

export const EmptyStateBox = styled(MotionBox)(({ theme }) => ({
  padding: '32px',
  textAlign: 'center',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor: theme.palette.background.neutral,
  color: theme.palette.text.secondary,
}))

// Same tonal file-type map as the Files table (TonalChip): VCF = info, FASTQ = primary,
// BAM = secondary; verification states use the status tones.
const FILE_TYPE_CHIP_TONE = {
  vcf: 'info',
  ont_bam: 'secondary',
  pacbio_bam: 'secondary',
  verifying: 'info',
  unverified: 'error',
  single: 'warning',
  verified: 'success',
  liftover: 'neutral',
}

export const FileTypeChip = styled(Box)(({ theme, fileType }) => {
  const colors = getToneColors(theme, FILE_TYPE_CHIP_TONE[fileType] || 'primary')
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    border: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    borderRadius: theme.shape.borderRadius,
    height: '24px',
    minWidth: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
  }
})

export const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '10px 16px',
  backgroundColor: theme.palette.background.neutral,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .search-field': {
    minWidth: '250px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: theme.palette.background.paper,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
}))

export const FileCountBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  padding: '8px 16px',
  borderRadius: '8px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}))

// File Selection Search and Filter Components
export const SearchFilterContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.neutral,
  borderRadius: theme.shape.borderRadiusMd,
  border: 'none',
  boxShadow: 'none',
}))

export const SearchFilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(1),
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
  },
}))

export const SearchFieldContainer = styled(Box)(({ theme }) => ({
  flex: '1 1 100%',
  minWidth: '250px',
  [theme.breakpoints.up('md')]: {
    flex: '1 1 300px',
  },
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '0.875rem',
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
}))

export const SomaticModeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: '1 1 100%',
  [theme.breakpoints.up('md')]: {
    flex: '0 0 auto',
  },
}))

export const FilterControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flex: '1 1 100%',
  [theme.breakpoints.up('md')]: {
    flex: '0 0 auto',
  },
}))

export const FilterLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
}))

export const FilterChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}))

export const FilterChip = styled(Box)(({ theme, active, variant = 'default' }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderColor: theme.palette.divider,
  height: '32px',
  padding: '4px 12px',
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 'fit-content',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.action.hover,
    boxShadow: 'none',
  },
}))

// Questionnaire Components - Apple-like design with glass morphism
export const QuestionnaireContainer = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflow: 'hidden',
  position: 'relative',
}))

export const QuestionnaireToggleContainer = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  position: 'relative',
}))

export const StepCard = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: alpha(theme.palette.grey[500], 0.4),
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.shape.borderRadiusMd,
  },
}))

export const StepNumberIndicator = styled(MotionBox)(({ theme, stepNumber }) => {
  const getBackgroundColor = () => {
    switch (stepNumber) {
      case 1:
        return theme.palette.primary.main
      case 2:
        return theme.palette.secondary.main
      case 3:
        return theme.palette.info.main
      case 4:
        return theme.palette.success.main
      default:
        return theme.palette.primary.main
    }
  }

  return {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: getBackgroundColor(),
    color: theme.palette.getContrastText(getBackgroundColor()),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.9rem',
    marginRight: theme.spacing(2),
    boxShadow: `0 4px 12px ${alpha(getBackgroundColor(), 0.25)}`,
    position: 'relative',
  }
})

export const QuestionnaireProgressContainer = styled(MotionBox)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}))

export const QuestionnaireProgressHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}))

export const QuestionnaireProgressTrack = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 6,
  backgroundColor: theme.palette.action.hover,
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow:
    theme.palette.mode === 'dark' ? 'inset 0 1px 3px rgba(0, 0, 0, 0.5)' : 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
}))

export const QuestionnaireProgressFill = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  borderRadius: 3,
  boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.25)}`,
}))

export const QuestionnaireStyledSwitch = styled(motion.div)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,
    '& + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 1,
    },
  },
  '& .MuiSwitch-track': {
    borderRadius: 22,
    backgroundColor: theme.palette.action.disabled,
    opacity: 1,
  },
  '& .MuiSwitch-thumb': {
    borderRadius: 20,
    width: 24,
    height: 24,
    boxShadow: 'none',
  },
  '& .MuiSwitch-switchBase': {
    padding: 9,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
    },
  },
}))

export const CompletionIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
  '& .MuiTypography-root': {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
}))

export const StandardModeContainer = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflow: 'hidden',
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  position: 'relative',
}))

export const StepIndicatorDot = styled(Box)(({ theme, active, completed }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: completed
    ? theme.palette.success.main
    : active
    ? theme.palette.primary.main
    : theme.palette.action.disabled,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {},
}))

export const StepIndicatorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}))

// Animation variants for questionnaire components
export const progressVariants = {
  hidden: { scaleX: 0, originX: 0 },
  visible: {
    scaleX: 1,
    transition: {
      duration: 0.8,
      ease: [0.4, 0, 0.2, 1],
    },
  },
}

export const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
}

export const questionnairePageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 380,
      damping: 30,
      mass: 0.8,
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
      mass: 0.6,
    },
  },
}

// Clinical Analysis Selection Components
export const ClinicalAnalysisContainer = styled(MotionBox)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'transparent',
  borderRadius: theme.shape.borderRadiusMd,
  position: 'relative',
  overflowX: 'hidden',
}))

export const ClinicalAnalysisCard = styled(MotionBox)(({ theme, selected }) => ({
  flex: 1,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
  background: selected ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '140px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  boxShadow: 'none',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    background: selected ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover,
  },
}))

export const ClinicalAnalysisIcon = styled(Box)(({ theme, selected }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.shape.borderRadiusMd,
  background: selected ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.16),
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  alignSelf: 'center',
  position: 'relative',
  boxShadow: 'none',
  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
}))

export const ClinicalAnalysisTitle = styled(Typography)(({ theme, selected }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(1),
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  transition: 'color 0.3s ease',
  lineHeight: 1.3,
  textAlign: 'center',
}))

export const ClinicalAnalysisDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  marginBottom: theme.spacing(2),
  flex: 1,
  textAlign: 'center',
  maxWidth: '90%',
}))

export const ClinicalAnalysisRequirement = styled(Typography)(({ theme, selected }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.success.dark : theme.palette.success.light,
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  transition: 'all 0.3s ease',
  marginTop: 'auto',
  textAlign: 'center',
}))

export const ClinicalModeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  flexDirection: 'row',
  marginTop: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}))

// Animation variants for clinical analysis selection
export const clinicalAnalysisVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.3 },
  },
}

export const clinicalCardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 350,
      damping: 25,
      mass: 0.8,
    },
  },
  hover: {
    y: -3,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
      mass: 0.6,
    },
  },
  tap: {
    y: -1,
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 15,
      mass: 0.5,
    },
  },
}

// Review Step Styled Components - Modern Design
export const ReviewContainer = styled(MotionBox)(({ theme }) => ({
  maxWidth: '1000px',
  margin: '0 auto',
  padding: theme.spacing(0, 2),
}))

export const ReviewHeader = styled(MotionBox)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3, 0),
  position: 'relative',
}))

// Unified Form Container
export const ReviewFormContainer = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${alpha(theme.palette.divider, 0.19)}`,
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
}))

// Form Section Headers
export const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&::before': {
    content: '""',
    width: 3,
    height: 16,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
    marginRight: theme.spacing(1),
  },
}))

// Form Rows for consistent layout
export const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '8px',
  backgroundColor: 'transparent',
  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  marginBottom: theme.spacing(0.75),
  minHeight: '44px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.background.neutral,
    borderColor: `${alpha(theme.palette.primary.main, 0.13)}`,
  },
}))

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  minWidth: '140px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  lineHeight: 1.4,
}))

export const FormValue = styled(Typography)(({ theme, isMonospace }) => ({
  fontWeight: 500,
  fontSize: '0.9rem',
  color: theme.palette.text.primary,
  flex: 1,
  textAlign: 'right',
  fontFamily: isMonospace ? theme.typography.fontFamilyMono : 'inherit',
  lineHeight: 1.4,
  wordBreak: 'break-word',
}))

export const FormChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  justifyContent: 'flex-end',
  alignItems: 'center',
  maxWidth: '60%',
}))

// File Cards within the form
export const FormFileCard = styled(Box)(({ theme, isPrimary }) => ({
  background: isPrimary
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(
        theme.palette.primary.main,
        0.01,
      )} 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)} 0%, ${alpha(
        theme.palette.secondary.main,
        0.01,
      )} 100%)`,
  borderRadius: '10px',
  border: isPrimary
    ? `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
    : `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: isPrimary
      ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.06)}`
      : `0 2px 8px ${alpha(theme.palette.secondary.main, 0.06)}`,
  },
}))

export const FormFileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
}))

export const FormFileBadge = styled(Chip)(({ theme, isPrimary }) => ({
  fontSize: '0.75rem',
  height: 20,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  backgroundColor: isPrimary ? theme.palette.primary.main : theme.palette.secondary.main,
  color: isPrimary ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}))

export const FormFileName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontSize: '0.85rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  wordBreak: 'break-all',
  lineHeight: 1.3,
}))

export const FormFileDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  marginTop: theme.spacing(0.25),
}))

// Clinical Data Text Boxes
export const ClinicalTextBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.neutral,
  borderRadius: '10px',
  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  marginBottom: theme.spacing(1),
}))

export const ClinicalTextLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.8rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.75),
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
}))

export const ClinicalTextContent = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}))

// Divider between sections
export const FormDivider = styled(Box)(({ theme }) => ({
  height: '1px',
  background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.19)}, transparent)`,
  margin: theme.spacing(2, 0),
}))

export const ReviewSection = styled(MotionBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${alpha(theme.palette.divider, 0.19)}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: 'none',
    borderColor: `${alpha(theme.palette.primary.main, 0.13)}`,
  },
}))

export const ReviewSectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.13)}`,
}))

export const ReviewSectionIcon = styled(Box)(({ theme, iconColor }) => ({
  width: 40,
  height: 40,
  borderRadius: theme.shape.borderRadiusMd,
  background: `linear-gradient(135deg, ${iconColor || theme.palette.primary.main} 0%, ${
    iconColor || theme.palette.primary.dark
  } 100%)`,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  boxShadow: `0 4px 16px ${alpha(iconColor || theme.palette.primary.main, 0.25)}`,
  '& .MuiSvgIcon-root': {
    fontSize: '1.4rem',
  },
}))

export const ReviewSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.2rem',
  color: theme.palette.text.primary,
  letterSpacing: '0.5px',
}))

export const ReviewSectionContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}))

export const ReviewInfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor: theme.palette.background.neutral,
  border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: `${alpha(theme.palette.primary.main, 0.13)}`,
  },
}))

export const ReviewInfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  minWidth: '140px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.4,
}))

export const ReviewInfoValue = styled(Typography)(({ theme, isMonospace }) => ({
  fontWeight: 500,
  fontSize: '0.95rem',
  color: theme.palette.text.primary,
  flex: 1,
  textAlign: 'right',
  fontFamily: isMonospace ? theme.typography.fontFamilyMono : 'inherit',
  lineHeight: 1.4,
  wordBreak: 'break-word',
}))

export const ReviewChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  justifyContent: 'flex-end',
  alignItems: 'center',
  maxWidth: '60%',
}))

export const ReviewChip = styled(Chip)(({ theme, chipColor = 'primary' }) => {
  // Handle 'default' case and fallback to a valid palette color
  const getChipColor = () => {
    if (chipColor === 'default') {
      return {
        main: theme.palette.text.secondary,
        light: theme.palette.action.selected,
      }
    }
    return theme.palette[chipColor] || theme.palette.primary
  }

  const colors = getChipColor()

  return {
    fontSize: '0.75rem',
    height: 22,
    fontWeight: 600,
    backgroundColor: `${alpha(colors.main, 0.07)}`,
    color: colors.main,
    border: `1px solid ${alpha(colors.main, 0.15)}`,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  }
})

export const ValidationSection = styled(MotionBox)(({ theme, severity = 'error' }) => {
  const severityColors = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
  }

  const color = severityColors[severity]

  return {
    background: `linear-gradient(145deg, ${alpha(color, 0.02)} 0%, ${alpha(color, 0.01)} 100%)`,
    borderRadius: theme.shape.borderRadiusMd,
    border: `1px solid ${alpha(color, 0.13)}`,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
  }
})

export const ValidationHeader = styled(Box)(({ theme, severity = 'error' }) => {
  const severityColors = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
  }

  return {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      color: severityColors[severity],
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
    },
    '& .MuiTypography-root': {
      color: severityColors[severity],
      fontWeight: 700,
      fontSize: '0.9rem',
    },
  }
})

export const ValidationList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(3),
}))

export const ValidationItem = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  display: 'flex',
  alignItems: 'flex-start',
  '&::before': {
    content: '"•"',
    marginRight: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
  },
}))

export const SubmitSection = styled(MotionBox)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3, 2),
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${alpha(theme.palette.divider, 0.19)}`,
  position: 'relative',
  overflow: 'hidden',
}))

export const SubmitButton = styled(MotionButton)(({ theme, canSubmit }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  fontSize: '1rem',
  padding: '12px 32px',
  minWidth: '200px',
  height: '48px',
  textTransform: 'none',
  letterSpacing: 0,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  background: canSubmit ? theme.palette.primary.main : theme.palette.action.disabledBackground,
  color: canSubmit ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  border: canSubmit ? 'none' : `1px solid ${theme.palette.divider}`,
  cursor: canSubmit ? 'pointer' : 'not-allowed',
  transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Performance optimizations
  willChange: 'transform, box-shadow, background',
  transform: 'translateZ(0)',
  backfaceVisibility: 'hidden',
  '&:hover': canSubmit
    ? {
        background: theme.palette.primary.dark,
        '&::before': {
          left: '100%',
        },
      }
    : {},
  '&:active': canSubmit ? {} : {},
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
}))

export const DiscardButton = styled(MotionButton)(({ theme }) => ({
  borderRadius: '14px',
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '12px 28px',
  minWidth: '160px',
  height: '50px',
  textTransform: 'none',
  letterSpacing: '0.3px',
  position: 'relative',
  overflow: 'hidden',
  background: 'transparent',
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Performance optimizations
  willChange: 'transform, box-shadow, border-color',
  transform: 'translateZ(0)',
  backfaceVisibility: 'hidden',
  '&:hover': {
    boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.13)}, 0 2px 8px rgba(0, 0, 0, 0.08)`,
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    background: `${alpha(theme.palette.error.main, 0.02)}`,
    '&::before': {
      left: '100%',
    },
  },
  '&:active': {},
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    fontSize: '1.1rem',
  },
}))

export const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
}))

// Confirmation Dialog Components - Optimized for performance
export const ConfirmationOverlay = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: theme.spacing(2),
  // Performance optimizations
  willChange: 'opacity',
  transform: 'translateZ(0)', // GPU acceleration
  backfaceVisibility: 'hidden',
}))

export const ConfirmationDialog = styled(motion.div)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${alpha(theme.palette.divider, 0.19)}`,
  padding: theme.spacing(3),
  maxWidth: '400px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  // Performance optimizations
  willChange: 'transform, opacity',
  transform: 'translateZ(0)', // GPU acceleration
  backfaceVisibility: 'hidden',
}))

export const ConfirmationHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2.5),
}))

export const ConfirmationTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1.5),
  letterSpacing: '0.3px',
}))

export const ConfirmationMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
  textAlign: 'center',
  marginBottom: theme.spacing(2.5),
}))

export const ConfirmationActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

export const ConfirmationButton = styled(MotionButton)(({ theme, variant }) => ({
  borderRadius: theme.shape.borderRadiusMd,
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: '10px 20px',
  minWidth: '100px',
  height: '40px',
  textTransform: 'none',
  letterSpacing: '0.3px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Performance optimizations
  willChange: 'transform, box-shadow',
  transform: 'translateZ(0)',
  backfaceVisibility: 'hidden',
  ...(variant === 'primary'
    ? {
        background: theme.palette.error.main,
        color: 'white',
        border: 'none',
        boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.19)}`,
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`,
          background: theme.palette.error.dark,
        },
      }
    : {
        background: 'transparent',
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
          boxShadow: 'none',
        },
      }),
  '&:active': {},
}))

export const FileInfoCard = styled(MotionBox)(({ theme, isPrimary }) => ({
  background: isPrimary
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
        theme.palette.primary.main,
        0.02,
      )} 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.07)} 0%, ${alpha(
        theme.palette.secondary.main,
        0.02,
      )} 100%)`,
  borderRadius: theme.shape.borderRadiusMd,
  border: isPrimary
    ? `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
    : `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: isPrimary
      ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.13)}`
      : `0 4px 16px ${alpha(theme.palette.secondary.main, 0.13)}`,
  },
}))

export const FileInfoHeader = styled(Box)(({ theme, isPrimary }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}))

export const FileTypeBadge = styled(Chip)(({ theme, isPrimary }) => ({
  fontSize: '0.75rem',
  height: 22,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  backgroundColor: isPrimary ? theme.palette.primary.main : theme.palette.secondary.main,
  color: isPrimary ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}))

export const FileName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontSize: '0.9rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  wordBreak: 'break-all',
  lineHeight: 1.4,
}))

export const FileDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  marginTop: theme.spacing(0.5),
}))

// Animation variants for review components
export const reviewSectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 350,
      damping: 25,
      mass: 0.8,
      delay: i * 0.1,
    },
  }),
}

export const submitButtonVariants = {
  idle: {
    y: 0,
    boxShadow: 'none',
  },
  hover: {
    y: -3,
    boxShadow: 'none',
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 25,
      mass: 0.4,
      velocity: 1,
    },
  },
  tap: {
    y: -1,
    transition: {
      type: 'spring',
      stiffness: 800,
      damping: 20,
      mass: 0.3,
    },
  },
  loading: {
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 30,
      mass: 0.5,
    },
  },
}

export const discardButtonVariants = {
  idle: {
    y: 0,
    boxShadow: 'none',
  },
  hover: {
    y: -2,
    boxShadow: 'none',
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 25,
      mass: 0.4,
      velocity: 1,
    },
  },
  tap: {
    y: -1,
    transition: {
      type: 'spring',
      stiffness: 800,
      damping: 20,
      mass: 0.3,
    },
  },
}

// Confirmation Dialog Animation Variants - Enhanced for smoother performance
export const confirmationOverlayVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

export const confirmationDialogVariants = {
  hidden: {
    opacity: 0,
    y: 16,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30,
      mass: 0.6,
      velocity: 2,
    },
  },
}

// Tooltip Content Styled Components
export const TooltipContainer = styled(Box)(({ theme, maxWidth = '420px' }) => ({
  padding: theme.spacing(2),
  maxWidth,
}))

export const TooltipHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
}))

export const TooltipTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  fontSize: '1rem',
  lineHeight: 1.3,
  color: 'white',
}))

export const TooltipSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  color: 'white',
  fontSize: '0.9rem',
}))

export const TooltipText = styled(Typography)(({ theme, isItalic = false }) => ({
  lineHeight: 1.5,
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '0.85rem',
  marginBottom: theme.spacing(2),
  fontStyle: isItalic ? 'italic' : 'normal',
}))

export const TooltipDescription = styled(Typography)(({ theme, isItalic = false }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '0.8rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  fontStyle: isItalic ? 'italic' : 'normal',
}))

// HPO Term Styled Components
export const HPOTermContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  maxWidth: '420px',
}))

export const HPOTermId = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.light,
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.8px',
  fontFamily: theme.typography.fontFamilyMono,
  display: 'block',
  marginBottom: theme.spacing(0.5),
}))

export const HPOCategoryBadge = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.25, 1),
  backgroundColor: alpha(theme.palette.info.main, 0.13),
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
  display: 'inline-block',
}))

export const HPOCategoryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.info.light,
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
}))

export const HPOObsoleteBadge = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.25, 1),
  backgroundColor: alpha(theme.palette.error.main, 0.13),
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
}))

export const HPOObsoleteText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.light,
  fontWeight: 600,
  fontSize: '0.75rem',
}))

export const SynonymsSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

export const SynonymsLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  display: 'block',
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))

export const SynonymChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.4, 1.5),
  backgroundColor: alpha(theme.palette.success.main, 0.15),
  borderRadius: '14px',
  border: `1px solid ${alpha(theme.palette.success.main, 0.31)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.21),
  },
}))

export const SynonymText = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.light,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.2,
}))

export const MoreChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.4, 1.5),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '14px',
  border: '1px solid rgba(255, 255, 255, 0.25)',
}))

export const MoreText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.75rem',
  fontWeight: 600,
}))

// Additional Info Section
export const AdditionalInfoSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
}))

export const InfoNote = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.75rem',
  display: 'block',
  marginBottom: theme.spacing(1),
  fontStyle: 'italic',
}))

export const InfoDate = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '0.75rem',
  display: 'block',
}))

export const HPOFooter = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
}))

export const HPOFooterText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.5)',
  fontSize: '0.75rem',
  fontStyle: 'italic',
  textAlign: 'center',
  display: 'block',
}))

export const ReplacementNotice = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.warning.main, 0.08),
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.warning.main, 0.19)}`,
}))

export const ReplacementText = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.light,
  fontSize: '0.75rem',
  fontWeight: 600,
}))

export const confirmationButtonVariants = {
  idle: {
    y: 0,
  },
  hover: {
    y: -2,
    transition: {
      type: 'spring',
      stiffness: 600,
      damping: 25,
      mass: 0.4,
      velocity: 1,
    },
  },
  tap: {
    y: -1,
    transition: {
      type: 'spring',
      stiffness: 800,
      damping: 20,
      mass: 0.3,
    },
  },
}

// Custom Scrollbar Styling
// ========================
//
// Available scrollbar components:
// 1. CustomScrollContainer - Standard scrollbar (8px) with primary color theme
// 2. ThinScrollContainer - Compact scrollbar (6px) for tight spaces
// 3. ElegantScrollContainer - Larger scrollbar (12px) with enhanced hover effects
// 4. StyledTableContainerWithScroll - Table-specific container with custom scrollbar
// 5. GlobalScrollbarStyles - Global scrollbar styling for entire app
//
// Usage examples:
// - <CustomScrollContainer sx={{ overflow: 'auto' }}>Content</CustomScrollContainer>
// - <ThinScrollContainer sx={{ overflow: 'auto' }}>Compact content</ThinScrollContainer>
// - <ElegantScrollContainer sx={{ overflow: 'auto' }}>Main content</ElegantScrollContainer>
// - <StyledTableContainerWithScroll>Table content</StyledTableContainerWithScroll>
// - Wrap entire app: <GlobalScrollbarStyles><App /></GlobalScrollbarStyles>
//
// Features:
// - Cross-browser compatibility (WebKit, Firefox, IE/Edge)
// - Theme-aware colors using Material-UI palette
// - Smooth hover animations and transitions
// - Responsive design with proper scaling
// - Dark/light mode support

export const CustomScrollContainer = styled(Box)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(
      theme.palette.primary.main,
      0.38,
    )} 100%)`,
    borderRadius: '4px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.38)} 0%, ${alpha(
        theme.palette.primary.main,
        0.5,
      )} 100%)`,
      borderColor: theme.palette.primary.main,
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
  // For IE and Edge
  '&::-ms-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-ms-scrollbar-track': {
    background: 'transparent',
  },
  '&::-ms-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.25),
    borderRadius: '4px',
  },
}))

// Thin Scrollbar for compact spaces
export const ThinScrollContainer = styled(Box)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[500], 0.4),
    borderRadius: '3px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      background: alpha(theme.palette.grey[500], 0.6),
      borderColor: theme.palette.primary.main,
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.primary.main, 0.19)} transparent`,
}))

// Elegant Scrollbar for main content areas
export const ElegantScrollContainer = styled(Box)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '12px',
    height: '12px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
    borderRadius: '6px',
    margin: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.13)} 0%, ${alpha(
      theme.palette.primary.main,
      0.25,
    )} 100%)`,
    borderRadius: '6px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    transition: 'background 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(
        theme.palette.primary.main,
        0.38,
      )} 100%)`,
      borderColor: theme.palette.primary.main,
    },
    '&:active': {
      background: alpha(theme.palette.grey[500], 0.6),
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.primary.main, 0.19)} transparent`,
}))

// Global Scrollbar Styles Component
export const GlobalScrollbarStyles = styled('div')(({ theme }) => ({
  // Global scrollbar styling for the entire application
  '& *::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '& *::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
    borderRadius: '5px',
    margin: '2px',
  },
  '& *::-webkit-scrollbar-thumb': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(
      theme.palette.primary.main,
      0.27,
    )} 100%)`,
    borderRadius: '5px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.27)} 0%, ${alpha(
        theme.palette.primary.main,
        0.4,
      )} 100%)`,
      borderColor: theme.palette.primary.main,
    },
  },
  '& *::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  // Firefox global scrollbar
  '& *': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${alpha(theme.palette.primary.main, 0.19)} transparent`,
  },
}))

// Enhanced Table Container with Custom Scrollbar
export const StyledTableContainerWithScroll = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'auto',
  boxShadow: 'none',
  // Fixed layout so the table always fits its container width instead of overflowing
  // horizontally (which pushed the rightmost 'Created' column out of view). The first
  // column (Select / Tumor-Normal) is pinned narrow; the rest share the remaining width
  // and clip overflow, so every column (including Created) stays visible.
  '& .MuiTable-root': {
    tableLayout: 'fixed',
    width: '100%',
  },
  '& .MuiTable-root .MuiTableCell-root': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiTable-root .MuiTableCell-root:first-of-type': {
    width: 72,
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '5px',
    margin: '2px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.grey[500], 0.4),
    borderRadius: '5px',
    border: 'none',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: alpha(theme.palette.grey[500], 0.6),
    },
    '&:active': {
      background: alpha(theme.palette.grey[500], 0.7),
    },
  },
  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.primary.main, 0.25)} transparent`,
  // For IE and Edge
  '&::-ms-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-ms-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '5px',
  },
  '&::-ms-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.25),
    borderRadius: '5px',
  },
  // Table styling
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.background.neutral,
    '& .MuiTableCell-head': {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.neutral,
      fontWeight: 600,
      fontSize: theme.typography.pxToRem(12.5),
      letterSpacing: '0.01em',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: 'none',
      borderLeft: 'none',
      padding: '16px 18px',
      '&:first-of-type': {
        paddingLeft: '32px',
      },
      '&:last-child': {
        paddingRight: '32px',
      },
    },
  },
  '& .MuiTableCell-root': {
    padding: '12px 18px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    borderLeft: 'none',
    color: theme.palette.text.primary,
    verticalAlign: 'middle',
    '&:first-of-type': {
      paddingLeft: '32px',
    },
    '&:last-child': {
      paddingRight: '32px',
    },
  },
  '& .MuiTableRow-root': {
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      '& .MuiTableCell-root:first-of-type': {
        paddingLeft: '28px',
      },
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '&.disabled': {
      backgroundColor: `${theme.palette.background.neutral} !important`,
      pointerEvents: 'none',
      '&:hover': {
        backgroundColor: `${theme.palette.background.neutral} !important`,
      },
      '& .MuiTableCell-root': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .MuiTypography-root': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .MuiRadio-root': {
        color: `${theme.palette.action.disabled} !important`,
      },
    },
  },
}))
